/**
 * [CCI-17658] Small accessors for the `slices[] + appliedIndex` slice-history
 * model on `AIContentPositionEntry` (see `pm-plugins/ai-session-context/types.ts`).
 *
 * They centralise the `noUncheckedIndexedAccess` fallback so callers always get a
 * concrete `Slice`, and encode the undo/redo availability rules in one place.
 *
 *   - `slices[0]`               = original (pre-AI) content.
 *   - `slices[appliedIndex]`    = the content currently at `[startPos, endPos]`.
 *   - undo available            = `appliedIndex > 0`.
 *   - redo available            = `appliedIndex < slices.length - 1`.
 */
import { Slice } from '@atlaskit/editor-prosemirror/model';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

/** The original (pre-AI) slice — `slices[0]`. Falls back to empty if absent. */
export const originalSliceOf = (entry: AIContentPositionEntry): Slice =>
	entry.slices[0] ?? Slice.empty;

/** The currently-applied slice — `slices[appliedIndex]`. Falls back to empty. */
export const appliedSliceOf = (entry: AIContentPositionEntry): Slice =>
	entry.slices[entry.appliedIndex] ?? Slice.empty;

/** Undo is available while the entry is not already at its original slice. */
export const entryCanUndo = (entry: AIContentPositionEntry): boolean => entry.appliedIndex > 0;

/** Redo is available while the entry is not already at its latest slice. */
export const entryCanRedo = (entry: AIContentPositionEntry): boolean =>
	entry.appliedIndex < entry.slices.length - 1;
